import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
} from '@angular/core';
import { NotificationRecord } from '../../models/notification-center.model';
import {
  resolveFamily,
  resolveEntity,
  relativeTime,
  dayBucket,
  resolveFacts,
  FamilyPresentation,
  NotificationFact,
} from '../../services/notification-center/notification-presentation';
import { isLinkable } from '../../services/entity-link/entity-link';

/** One row, with everything the template needs already worked out. */
interface TileRow {
  notification: NotificationRecord;
  presentation: FamilyPresentation;
  relativeTime: string;
  absoluteTime: string;
  unread: boolean;
  linkable: boolean;
  /** Short facts shown as pills under the body. */
  facts: NotificationFact[];
  /** Title with markup stripped, for the accessible label. */
  plainTitle: string;
}

/**
 * A run of same-family notifications collapsed into one row.
 *
 * Exports are the case this exists for: every system notification observed in
 * the live data is an export, and a batch job produces them a dozen at a time.
 * Eleven near-identical rows push everything else off the screen, which is the
 * exact failure macOS solves by stacking.
 */
interface TileStack {
  /** Stable identity — day + family. Survives new arrivals; see expandedStacks. */
  key: string;
  /** The rows this stands in for, newest first. */
  items: TileRow[];
  label: string;
  relativeTime: string;
  expanded: boolean;
}

interface TileGroup {
  label: string;
  /** Rows in display order; a stack stands where its run began. */
  entries: TileEntry[];
  /** Flat list of every row in the group, for the day-level Clear. */
  items: TileRow[];
}

/**
 * Either a single row or a collapsed run of them.
 *
 * `key` is precomputed rather than derived in the template, because @for's
 * track expression runs on every change detection pass and a stack and its
 * members must never collide on one.
 */
type TileEntry =
  | { kind: 'row'; key: string; row: TileRow; stacked: boolean }
  | { kind: 'stack'; key: string; stack: TileStack };

/** Day buckets in the order they should appear, newest first. */
const BUCKET_ORDER = ['Today', 'Yesterday', 'This week', 'Earlier'];

/**
 * How many consecutive same-family rows it takes before collapsing them.
 *
 * Three is deliberate. At two, collapsing costs the reader a click to see
 * something that would have fitted anyway; by four the run is already crowding
 * the day out.
 */
const STACK_THRESHOLD = 3;

/** Families worth stacking — high-volume and individually low-value. */
const STACKABLE = new Set(['system']);

/**
 * Where the open/closed state of a stack is remembered.
 *
 * localStorage rather than sessionStorage: the complaint this fixes is that the
 * panel always reopened collapsed, and a reader who keeps their exports visible
 * means it across sessions, not just this tab.
 */
const EXPANDED_KEY = 'colobbo.notifications.expandedStacks';

/** Never let a storage failure break the panel — private mode, quota, or a stale value. */
function loadExpandedStacks(): string[] {
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function saveExpandedStacks(keys: Set<string>): void {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...keys]));
  } catch {
    // Storage unavailable — the stack still toggles for this view, it just
    // will not be remembered. Losing the preference must not lose the click.
  }
}

@Component({
  selector: 'app-notification-tile-list',
  templateUrl: './notification-tile-list.component.html',
  styleUrls: ['./notification-tile-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
// eslint-disable-next-line @angular-eslint/prefer-standalone -- declared in SharedModule alongside 250+ siblings; NgModule migration is a repo-wide ship, see docs/eslint-baseline.md
  standalone: false,
})
export class NotificationTileListComponent implements OnChanges {
  @Input() notifications: NotificationRecord[] = [];
  @Input() loading = false;
  @Input() error: string | null = null;
  @Input() expandedId: string | null = null;

  @Output() notificationSelected = new EventEmitter<NotificationRecord>();
  /** A whole day cleared at once, from the Clear on its heading. */
  @Output() groupCleared = new EventEmitter<NotificationRecord[]>();
  /** Marked read in place, without opening the record. */
  @Output() markedRead = new EventEmitter<NotificationRecord>();
  /** Removed from the list entirely. */
  @Output() dismissed = new EventEmitter<NotificationRecord>();

  /**
   * Rows are derived once per input change rather than through template
   * function calls — with OnPush and a 50-row list, calling resolveFamily from
   * the template would re-run it on every check.
   */
  groups: TileGroup[] = [];

  /**
   * Stacks the reader has opened.
   *
   * Keyed by DAY + FAMILY, not by the anchor row's id. The anchor is whichever
   * notification currently sits at the top of the run, so keying on it meant the
   * stack silently re-collapsed the moment a newer one arrived — the reader had
   * opened "today's exports", and the thing they opened no longer existed.
   *
   * Persisted, because the component is destroyed when the panel closes: an
   * in-memory Set meant re-opening the panel always started collapsed, so a
   * reader who wanted their exports visible had to press Show every single time.
   */
  private expandedStacks = new Set<string>(loadExpandedStacks());

  ngOnChanges(): void {
    this.groups = this.buildGroups(this.notifications ?? []);
  }

  toggleStack(stack: TileStack, event: MouseEvent): void {
    event.stopPropagation();
    const key = stack.key;
    if (!key) return;
    if (this.expandedStacks.has(key)) this.expandedStacks.delete(key);
    else this.expandedStacks.add(key);
    saveExpandedStacks(this.expandedStacks);
    this.groups = this.buildGroups(this.notifications ?? []);
  }

  private buildGroups(records: NotificationRecord[]): TileGroup[] {
    const now = new Date();
    const byBucket = new Map<string, TileRow[]>();

    for (const notification of records) {
      const entity = resolveEntity(notification);
      const row: TileRow = {
        notification,
        presentation: resolveFamily(notification),
        relativeTime: relativeTime(notification.createdAt, now),
        absoluteTime: new Date(notification.createdAt).toLocaleString(),
        unread: !notification.readAt,
        linkable: isLinkable(entity),
        facts: resolveFacts(notification),
        plainTitle: String(notification.title ?? '').replace(/<[^>]*>/g, '').trim(),
      };
      const bucket = dayBucket(notification.createdAt, now);
      const list = byBucket.get(bucket);
      if (list) list.push(row);
      else byBucket.set(bucket, [row]);
    }

    return BUCKET_ORDER
      .filter((label) => byBucket.has(label))
      .map((label) => {
        const items = byBucket.get(label) as TileRow[];
        return { label, items, entries: this.collapseRuns(items, label) };
      });
  }

  /**
   * Collapse a stackable family into ONE entry per day.
   *
   * "Today's exports" is a single thing to the reader, so it is a single stack.
   * That is also what the label promises: a stack saying "5 exports" next to
   * three more export rows further down the same day is not a summary of
   * anything.
   *
   * THIS REPLACES CONSECUTIVE-RUN GROUPING, which was a deliberate decision
   * here and is worth saying why it went. Only adjacent rows used to collapse,
   * to keep the timeline from reordering. But adjacency is an accident of what
   * else happened to arrive in between: one work-order notification landing
   * mid-batch split a day's exports into two stacks, and a run that fell under
   * the threshold stopped collapsing at all and left loose export rows
   * scattered through the day. Both stacks also answered to the same key, so
   * pressing Show on one opened the other, and the two entries collided on
   * their @for track key — which makes Angular reuse the wrong DOM node, and is
   * why the toggle looked like it did nothing.
   *
   * Grouping the whole day does move exports out of strict time order relative
   * to other families. That is a real cost and it is the smaller one: these are
   * the rows the reader has already said are individually low-value by letting
   * them be stacked, the stack is collapsed by default, and it carries the time
   * of its most recent member. Everything else keeps its order.
   */
  private collapseRuns(rows: TileRow[], dayLabel: string): TileEntry[] {
    // Every stackable family in this day, in the order each first appears.
    const byFamily = new Map<string, TileRow[]>();
    for (const row of rows) {
      const family = row.presentation.family;
      if (!STACKABLE.has(family)) continue;
      const seen = byFamily.get(family);
      if (seen) seen.push(row);
      else byFamily.set(family, [row]);
    }

    // Below the threshold a family is not worth collapsing, so it stays inline
    // and keeps its exact place in the timeline.
    const stacked = new Set<string>();
    for (const [family, items] of byFamily) {
      if (items.length >= STACK_THRESHOLD) stacked.add(family);
    }

    const entries: TileEntry[] = [];
    const emitted = new Set<string>();

    for (const row of rows) {
      const family = row.presentation.family;
      if (!stacked.has(family)) {
        entries.push({ kind: 'row', key: row.notification.id, row, stacked: false });
        continue;
      }

      // The stack stands where its family FIRST appears in the day, so the
      // group sits at the point in the timeline the reader saw it start.
      if (emitted.has(family)) continue;
      emitted.add(family);

      const items = byFamily.get(family) as TileRow[];
      // Day + family is unique by construction now — one stack per family per
      // day — so it cannot collide, and it survives new arrivals: the reader
      // opened "today's exports" and that is still the same thing when another
      // export lands.
      const stackKey = `${dayLabel}:${family}`;
      const expanded = this.expandedStacks.has(stackKey);
      entries.push({
        kind: 'stack',
        key: `stack-${stackKey}`,
        stack: {
          key: stackKey,
          items,
          label: `${items.length} ${items[0].presentation.label.toLowerCase()}s`,
          relativeTime: items[0].relativeTime,
          expanded,
        },
      });
      if (expanded) {
        for (const item of items) {
          entries.push({ kind: 'row', key: item.notification.id, row: item, stacked: true });
        }
      }
    }

    return entries;
  }

  /**
   * The Open link — the only control in a row that navigates.
   *
   * Marking read happens downstream as a side effect of going there, so a
   * notification you act on never also needs clearing. Was `onToggle`, bound to
   * the whole row.
   */
  onOpen(notification: NotificationRecord, event: MouseEvent): void {
    event.stopPropagation();
    this.notificationSelected.emit(notification);
  }

  /**
   * The row itself. Marks read in place and goes nowhere.
   *
   * This is the whole point of the split: clearing a backlog is now one click
   * per row with the panel still open, instead of one navigation and one reopen
   * per row. It used to be a 20px tick in the corner, which is a small target
   * for the thing people do most.
   */
  onMarkRead(notification: NotificationRecord, event: MouseEvent): void {
    event.stopPropagation();
    this.markedRead.emit(notification);
  }

  onDismiss(notification: NotificationRecord, event: MouseEvent): void {
    event.stopPropagation();
    this.dismissed.emit(notification);
  }

  onClearGroup(group: TileGroup, event: MouseEvent): void {
    event.stopPropagation();
    this.groupCleared.emit(group.items.map((item) => item.notification));
  }

  isUnread(notification: NotificationRecord): boolean {
    return !notification.readAt;
  }
}
